import Pristine from 'pristinejs';
import { addRecaptchaFields, getCaptchaAction, loadRecaptcha } from '../captcha/google';
import { getDeviceInfo } from '../user/device-info';
import newValidator, { FormValidationError } from '../validation';
import { setupEmailSuggestionFromResult } from '../validation/email-suggest';
import triggerEvent, { GA4Event, GAEventName, GAEventParameters } from '../analytics/ga/events';
import { ErrorType, ErrorEvent } from '../analytics/ga/events';
import { fillTemplatedEventsFromFormSubmission, TemplateAnalyticsHeader } from '../analytics/http';
import { FormUpdateFrequency, FormUpdateMailings, getFormInfo } from '../forms/types';
import { NewContentLoadedEvent } from '../events/new-content-loaded';
import Logger from '../logging/logger';
import { parseBool } from '../strconv/boolean';
import { acceptPrivacyConsent } from '../privacy-consent';

const handledForms: Array<string> = [
    FormUpdateMailings,
    FormUpdateFrequency,
];

export type SimpleFormSuccessEventDetail<TResponse = unknown> = {
    formName: string;
    form: HTMLFormElement;
    response: TResponse;
}

export function initGenericForm( form: HTMLFormElement ) {
    let validator: Pristine | null = null;

    if ( form.dataset.initialised && form.dataset.initialised === 'true' ) {
        return;
    }

    form.dataset.initialised = 'true';

    const formInfo = getFormInfo( form );

    Logger.info( 'initialised new generic form', formInfo.name );

    if ( form.dataset.simpleFormValidate && form.dataset.simpleFormValidate === 'true' ) {
        validator = newValidator( form );
    }

    let messageTimeout: number | null = null;

    form.addEventListener( 'click', ( event: Event ) => {
        if ( !event.target || !( event.target instanceof HTMLButtonElement ) || !event.target.matches( '[data-simple-form-confirm]' ) ) {
            return;
        }

        const inputName = event.target.dataset.simpleFormConfirm;

        if ( !inputName ) {
            return;
        }

        const input = document.createElement( 'input' );
        input.hidden = true;
        input.name = inputName;
        input.value = 'true';

        form.append( input );

        const parentAlert = event.target.closest( '.alert' );

        if ( parentAlert ) {
            parentAlert.remove();
        }

        handleForm();
    } );

    const handleForm = async function( event?: SubmitEvent ) {
        if ( event ) {
            event.preventDefault();
        }

        if ( form.dataset.isProcessing && form.dataset.isProcessing === 'true' ) {
            return;
        }

        const messageContainer = form.querySelector( '.messages' );

        if ( !messageContainer ) {
            throw new Error( 'failed to find message container for form' );
        }

        const genericErrContainer = form.querySelector<HTMLElement>( '.general-error-container' );

        if ( genericErrContainer ) {
            genericErrContainer.classList.add( 'd-none' );
        }

        messageContainer.innerHTML = '';

        if ( messageTimeout ) {
            window.clearTimeout( messageTimeout );
            messageTimeout = null;
        }

        form.dataset.isProcessing = 'true';

        const submitBtn = form.querySelector( 'button[type=submit]' ) as HTMLButtonElement;

        if ( !submitBtn ) {
            throw new Error( 'failed to find form submit button' );
        }

        submitBtn.disabled = true;
        submitBtn.classList.add( 'btn--loading' );

        if ( validator !== null ) {
            const valid = validator.validate();

            const errors: Array<FormValidationError> = validator.getErrors();

            if ( errors.length > 0 ) {
                errors.forEach( ( err: FormValidationError ) => {
                    triggerEvent<ErrorEvent>( GAEventName.Error, {
                        'error_type': ErrorType.ErrForm,
                        'error_message': err.errors[ 0 ],
                        'engaged_target': err.input.value,
                        'engaged_element': err.input.name,
                    } );
                } );
            }

            if ( !valid ) {
                form.dataset.isProcessing = 'false';
                submitBtn.disabled = false;
                submitBtn.classList.remove( 'btn--loading' );

                if ( genericErrContainer ) {
                    genericErrContainer.classList.remove( 'd-none' );
                }

                return;
            }
        }

        const reset = function() {
            form.querySelectorAll( 'input' ).forEach( ( formInput: HTMLInputElement ) => {
                if ( formInput.type === 'hidden' || formInput.dataset.resetableWithForm === 'false' ) {
                    return;
                }

                if ( formInput.type === 'checkbox' ) {
                    if ( formInput.dataset.default ) {
                        formInput.checked = parseBool( formInput.dataset.default );
                    } else {
                        formInput.checked = false;
                    }
                } else if ( formInput.value && formInput.value.length > 0 ) {
                    formInput.value = '';
                }
            } );

            form.querySelectorAll( 'select' ).forEach( ( select: HTMLSelectElement ) => {
                if ( select.dataset.defaultSelected ) {
                    select.value = select.dataset.defaultSelected;
                } else {
                    select.value = '';
                }
            } );

            form.querySelectorAll( 'textarea' ).forEach( ( txt: HTMLTextAreaElement ) => {
                if ( txt.dataset.defaultValue && txt.dataset.defaultValue.length > 0 ) {
                    txt.value = txt.dataset.defaultValue;
                } else {
                    txt.value = '';
                }
            } );
        };

        const sendRequest = async function() {
            const formData = new FormData( form );
            formData.append( 'deviceinfo', JSON.stringify( getDeviceInfo() ) );

            const url = new URL( window.location.origin + form.getAttribute( 'action' ) );

            const response = await window.http( url, {
                method: 'POST',
                body: formData,
            } );

            form.dataset.isProcessing = 'false';
            submitBtn.classList.remove( 'btn--loading' );

            if ( response.headers.get( TemplateAnalyticsHeader ) ) {
                const eventTemplates = JSON.parse( response.headers.get( TemplateAnalyticsHeader ) as string );

                const events = fillTemplatedEventsFromFormSubmission( eventTemplates, {
                    form: form,
                    event: event,
                } );

                events.forEach( async ( event: GA4Event ) => {
                    await triggerEvent( event.event, event.parameters );
                } );
            }

            if ( !response.ok ) {
                throw new Error( `request for ${url} failed with status ${response.status}` );
            }

            const data = await response.json();

            const successful = ( data.success || data.refresh || data.redirect );

            if ( data.success ) {
                acceptPrivacyConsent( form );
            }

            const handled = handledForms.includes( formInfo.name );

            if ( successful && handled ) {
                document.dispatchEvent( new CustomEvent( 'twc:' + formInfo.name, {
                    bubbles: true,
                    detail: {
                        formName: formInfo.name,
                        form: form,
                    },
                } ) );
            }

            if ( data.success && form.dataset.simpleFormResetOnSubmit && form.dataset.simpleFormResetOnSubmit === 'true' ) {
                if ( form.dataset.simpleFormResetEvent && form.dataset.simpleFormResetEvent ) {
                    form.dispatchEvent( new CustomEvent( 'twc:reset', {
                        bubbles: true,
                    } ) );
                } else {
                    reset();
                }
            }

            let htmlContent = '';

            if ( data.content ) {
                htmlContent = data.content;
            } else if ( data.html ) {
                htmlContent = data.html;
            }

            if ( data.success && htmlContent ) {
                submitBtn.disabled = false;
                document.body.insertAdjacentHTML( 'beforeend', htmlContent );
            }

            const removeSubmitOnSuccess = form.dataset.simpleFormBlockOnSuccess === 'true';

            if ( data.success && !htmlContent && !removeSubmitOnSuccess ) {
                messageContainer.insertAdjacentHTML( 'beforeend', `<div class="alert alert-success simpleform__successmsg">${data.message}</div>` );

                if ( form.dataset.simpleFormSuccessTimeout ) {
                    messageTimeout = window.setTimeout( () => {
                        messageContainer.innerHTML = '';
                    }, parseInt( form.dataset.simpleFormSuccessTimeout || '5000' ) );
                }
            }

            if ( data.success && removeSubmitOnSuccess ) {
                const submitBtnParent = submitBtn.parentElement;

                if ( !submitBtnParent ) {
                    throw new Error( 'failed to find parent element of submit button' );
                }

                submitBtn.classList.add( 'd-none' );

                const resetButton = document.createElement( 'button' );
                resetButton.type = 'button';
                resetButton.innerHTML = form.dataset.simpleFormBlockOnSuccessBtn as string;

                const msg = document.createElement( 'div' );
                msg.className = 'alert alert-success simpleform__successmsg mb-none';

                if ( data.message ) {
                    msg.innerHTML = data.message + ' ';
                }

                msg.appendChild( resetButton );

                resetButton.addEventListener( 'click', ( event: Event ) => {
                    reset();
                    submitBtn.classList.remove( 'd-none' );
                    msg.remove();
                } );

                submitBtn.insertAdjacentElement( 'afterend', msg );
            }

            if ( data.success && form.dataset.simpleFormOnSuccess && form.dataset.simpleFormOnSuccess.length > 0 ) {
                const events = form.dataset.simpleFormOnSuccess.split( ',' );
                events.forEach( ( event: string ) => form.dispatchEvent( new CustomEvent<SimpleFormSuccessEventDetail>( event, {
                    bubbles: true,
                    detail: {
                        formName: formInfo.name,
                        form: form,
                        response: data,
                    },
                } ) ) );
            }

            if ( !data.success && data.refresh ) {
                window.location.reload();
            }

            if ( !data.success && data.redirect ) {
                window.location = data.redirect;
            }

            if ( !data.success && data.errors && data.errors.form ) {
                triggerEvent<ErrorEvent>( GAEventName.Error, {
                    'error_type': ErrorType.ErrServer,
                    'error_message': data.errors.form,
                } );

                messageContainer.insertAdjacentHTML( 'beforeend', `<div class="alert alert-danger">${data.errors.form}</div>` );
                window.scroll( 0, messageContainer.getBoundingClientRect().top + window.scrollY - 200 );
            }

            if ( data.didyoumean ) {
                triggerEvent<ErrorEvent>( GAEventName.Error, {
                    'error_type': ErrorType.ErrForm,
                    'error_message': 'did-you-mean',
                    'engaged_target': `${formData.get( data.inputName || 'email' ) || ''}>>${data.didyoumean}`,
                } );

                setupEmailSuggestionFromResult( {
                    emailSuggestion: data.didyoumean,
                    html: data.didyoumeanhtml,
                    inputName: data.inputName || '',
                }, form );

                if ( genericErrContainer ) {
                    genericErrContainer.classList.remove( 'd-none' );
                }
            }

            if ( data.fileToDownload ) {
                const filedownloadURL = new URL( data.fileToDownload );
                window.location.assign( window.location.origin + '/cdnproxy' + filedownloadURL.pathname );
            }

            if ( data.events && Object.keys( data.events ).length > 0 ) {
                Object.keys( data.events as Record<string, GAEventParameters> ).forEach( ( eventName: string ) => {
                    const eventData = {
                        'form_type': formInfo.name,
                        'form_location': formInfo.location,
                        ...data.events[ eventName ],
                    };

                    triggerEvent( eventName as GAEventName, eventData );
                } );
            }

            submitBtn.disabled = false;
        };

        const token = await loadRecaptcha( getCaptchaAction( form ) );

        addRecaptchaFields( form, token, getCaptchaAction( form ) );

        sendRequest();
    };

    form.addEventListener( 'submit', handleForm );
}

export function initGenericForms( container: HTMLElement | Document = document, addEventListener: boolean = false ): void {
    function init( c: HTMLElement | Document = document ) {
        const forms = c.querySelectorAll<HTMLFormElement>( '.twc__simple-form' );
        forms.forEach( ( form: HTMLFormElement ) => initGenericForm( form ) );
    }

    if ( addEventListener ) {
        window.addEventListener( 'twc:newcontentloaded', ( event: CustomEvent<NewContentLoadedEvent> ) => {
            let container: HTMLElement | Document = document;

            if ( event && event.detail && event.detail.container ) {
                container = event.detail.container;
            }

            init( container );
        } );
    }

    init( container );
}
